/* eslint-disable react/jsx-key */
import {
  getCoreRowModel,
  useReactTable,
  flexRender,
  ColumnDef,
} from "@tanstack/react-table";
import React from "react";
import PaginationFooter from "../PaginationFooter/PaginationFooter";
import { useTranslation } from "react-i18next";
import FiltersBar from "../FiltersBar/FiltersBar";
import classNames from "classnames";
import ActionsBar from "../ActionsBar/ActionsBar";

export interface TableAction {
  label: string;
  onClick: () => void;
  visible?: boolean;
  icon?: string;
  type?: "primary" | "secondary" | "accent" | "error" | "base" | "info" | "success" | "warning";
  variant?: "filled" | "outlined" | "ghost";
}

interface TableProps {
  columns: ColumnDef<any>[];
  data: object[];
  onRowClick?: (row: any) => void;
  pagination?: {
    onPageChange: () => void;
    metadata?: any;
  };
  filterBar?: {
    onSearch: (query: string) => void;
    onClear: () => void;
    onFilter: (filter: any) => void;
    searchAvailable: boolean;
    filters: any;
  };
  actions?: TableAction[];
  /** @deprecated use density instead */
  size?: "sm" | "md";
  density?: "comfortable" | "compact" | "dense";
}

export const Table = ({
  columns,
  data = [],
  pagination,
  onRowClick,
  filterBar,
  actions,
  size,
  density,
}: TableProps) => {
  const [t] = useTranslation();
  const tableInstance = useReactTable({
    data: Array.isArray(data) ? data : [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const resolvedDensity = density || (size === "sm" ? "compact" : "comfortable");

  const headerPadding = {
    comfortable: "px-4 py-3 text-sm",
    compact: "px-3 py-2.5 text-xs",
    dense: "px-2 py-1.5 text-xs",
  };

  const cellPadding = {
    comfortable: "px-4 py-3.5 text-base",
    compact: "px-3 py-2 text-sm",
    dense: "px-2 py-1.5 text-sm",
  };

  const { getRowModel, getHeaderGroups } = tableInstance;
  const rows = getRowModel().rows || [];
  const getCellTitle = (cell: any) => {
    const meta = cell?.column?.columnDef?.meta as any;
    if (meta?.getTitle) {
      return meta.getTitle(cell);
    }
    const value = cell?.getValue?.();
    if (value === null || value === undefined) return undefined;
    if (["string", "number", "boolean"].includes(typeof value)) {
      return String(value);
    }
    return undefined;
  };

  return (
    <div className="flex flex-col font-sans">
      <div className="mb-2 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        {filterBar && <FiltersBar config={filterBar} />}
        {actions && <ActionsBar actions={actions} />}
      </div>
      <div className="-my-2">
        <div className="inline-block w-full py-2 align-middle">
          <div className="rounded-xl border border-primary-100 bg-white/50 dark:border-primary-800/60 dark:bg-content-900/40 backdrop-blur-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full table-fixed divide-y divide-primary-200/60 dark:divide-primary-800/50">
                <thead className="bg-primary-50/80 dark:bg-primary-900/40">
                  {getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className={classNames(
                            "truncate whitespace-nowrap text-left font-medium text-primary-900/80 dark:text-primary-100/80",
                            headerPadding[resolvedDensity]
                          )}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-primary-100/50 dark:divide-primary-800/40">
                  {rows.map((row) => {
                    return (
                      <tr
                        className={classNames(
                          "transition-colors even:bg-transparent odd:bg-primary-50/30 dark:odd:bg-primary-900/15",
                          onRowClick &&
                            "cursor-pointer hover:bg-primary-50/80 dark:hover:bg-primary-900/30",
                        )}
                        key={row.id}
                        onClick={() => onRowClick && onRowClick(row?.original)}
                      >
                        {row.getVisibleCells().map((cell) => {
                          const meta = cell?.column?.columnDef?.meta as any;

                          return (
                            <td
                              key={cell.id}
                              title={getCellTitle(cell)}
                              className={classNames(
                                "text-primary-900 dark:text-primary-100",
                                cellPadding[resolvedDensity]
                              )}
                            >
                              <div
                                className={classNames(
                                  meta?.wrap
                                    ? "whitespace-normal"
                                    : "truncate whitespace-nowrap",
                                )}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext(),
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {rows.length <= 0 && (
              <div className="flex w-full flex-col items-center justify-center py-8 text-center">
                <div className="icon-[ph--folder-simple] text-primary-200 dark:text-primary-700 mb-2 h-8 w-8" />
                <div className="text-primary-600 dark:text-primary-400 text-sm">
                  {t("global:shared.Table.empty", "No records yet.")}
                </div>
              </div>
            )}
            {pagination?.metadata && (
              <PaginationFooter transparent {...pagination} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
